const SELECTED_SITE_STORAGE_KEY = 'selected_site_id';

// Marcador explicito de "Consolidado" (ver todas las sucursales juntas).
// Sin esto no habia forma de distinguir "el usuario eligio Consolidado" de
// "el usuario nunca eligio nada": ambos casos guardaban vacio, y ademas el
// vacio BORRABA la preferencia global, que es compartida por todas las orgs.
export const CONSOLIDATED_SITE_ID = '__consolidado__';

const canUseLocalStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const getOrganizationScopedStorageKey = (organizationId) => {
  const safeOrganizationId = String(organizationId || '').trim();
  return safeOrganizationId
    ? `${SELECTED_SITE_STORAGE_KEY}:${safeOrganizationId}`
    : SELECTED_SITE_STORAGE_KEY;
};

/**
 * Devuelve la preferencia guardada para esa organizacion.
 * Puede devolver CONSOLIDATED_SITE_ID si el usuario eligio "Consolidado".
 *
 * La clave global (`selected_site_id`, sin org) quedo como LEGADO: solo se lee
 * cuando la org todavia no tiene clave propia, para migrar sin que el usuario
 * pierda su eleccion. Nunca se usa para decidir entre organizaciones distintas
 * (quien llama valida el id contra los sitios de la org actual).
 */
export const getStoredSelectedSiteId = (organizationId = '') => {
  if (!canUseLocalStorage()) return '';
  try {
    const scopedKey = getOrganizationScopedStorageKey(organizationId);
    const scoped = window.localStorage.getItem(scopedKey);
    if (scoped) return scoped;
    return window.localStorage.getItem(SELECTED_SITE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

export const isConsolidatedPreference = (value) => String(value || '') === CONSOLIDATED_SITE_ID;

/**
 * Persiste la eleccion del usuario para ESA organizacion.
 * `siteId` vacio significa "Consolidado" y se guarda como tal.
 *
 * Antes, guardar vacio hacia removeItem de la clave scoped Y de la global: un
 * usuario que elegia Consolidado en una org se quedaba sin preferencia en TODAS
 * las demas, y al volver a entrar el selector aparecia vacio.
 */
export const persistSelectedSiteId = (siteId, organizationId = '') => {
  if (!canUseLocalStorage()) return;
  try {
    const scopedKey = getOrganizationScopedStorageKey(organizationId);
    const value = siteId || CONSOLIDATED_SITE_ID;
    window.localStorage.setItem(scopedKey, value);

    // La clave global se mantiene al dia solo como compatibilidad hacia atras
    // (codigo viejo que la lea sin org). Nunca se borra desde aca.
    if (siteId && scopedKey !== SELECTED_SITE_STORAGE_KEY) {
      window.localStorage.setItem(SELECTED_SITE_STORAGE_KEY, siteId);
    }
  } catch {
    // ignore storage errors
  }
};

/** Olvida la preferencia de esa org (vuelve a "sin elegir", no a Consolidado). */
export const clearStoredSelectedSiteId = (organizationId = '') => {
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.removeItem(getOrganizationScopedStorageKey(organizationId));
  } catch {
    // ignore storage errors
  }
};

export const isValidSiteId = (sites, siteId) => {
  if (!siteId) return false;
  return (Array.isArray(sites) ? sites : []).some((site) => String(site?.id || '') === String(siteId));
};

/**
 * Resuelve que sucursal mostrar. Orden de prioridad:
 *   1. Una sola sucursal disponible -> esa, siempre (no hay nada que elegir).
 *   2. La seleccion en memoria, si sigue siendo valida.
 *   3. La preferencia guardada de esa org (incluye "Consolidado" explicito).
 *   4. `fallbackSiteId` (ej: default_site_id de la organizacion).
 *   5. `fallbackToFirst` -> la primera. Apagado por defecto a proposito.
 */
export const resolvePreferredSiteId = (
  sites,
  { currentSiteId = '', fallbackSiteId = '', fallbackToFirst = false, organizationId = '' } = {},
) => {
  const safeSites = Array.isArray(sites) ? sites : [];
  if (safeSites.length === 0) return '';

  if (safeSites.length === 1) {
    return safeSites[0]?.id || '';
  }

  if (isValidSiteId(safeSites, currentSiteId)) {
    return currentSiteId;
  }

  const storedSiteId = getStoredSelectedSiteId(organizationId);
  // Consolidado es una eleccion valida y se respeta: no seguimos buscando.
  if (isConsolidatedPreference(storedSiteId)) return '';
  if (isValidSiteId(safeSites, storedSiteId)) {
    return storedSiteId;
  }

  if (isValidSiteId(safeSites, fallbackSiteId)) {
    return fallbackSiteId;
  }

  if (fallbackToFirst) {
    return safeSites[0]?.id || '';
  }

  return '';
};
